import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { Button, Form } from 'semantic-ui-react';
import InputMask from "react-input-mask";
import { useDidUpdate, useToggle } from '../../lib/hooks';
import { Input, Popup } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './DueDateEditStep.module.scss';

const DueDateEditStep = React.memo(({ defaultValue, onUpdate, onBack, onClose }) => {
  const [t] = useTranslation();
  const datePickerDateFormat = t('format:datePickerDate', { defaultValue: 'yyyy.MM.dd' });
  const datePickerDateTimeFormat = t('format:datePickerDateTime', {
    defaultValue: 'yyyy.MM.dd HH:mm',
  });
  
  const [data, handleFieldChange, setData] = useForm(() => {
    const date = defaultValue || new Date().setHours(12, 0, 0, 0);
    return {
      date: t('format:datePickerDate', {
        defaultValue: datePickerDateFormat,
        postProcess: 'formatDate',
        value: date,
      }),
      time: t('format:time', {
        postProcess: 'formatDate',
        value: date,
      }),
    };
  });

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    const date = t('format:datePickerDate', {
      defaultValue: datePickerDateFormat,
      postProcess: 'parseDate',
      value: data.date,
    });

    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    // Backward compatibility: allow previous locale-specific format (e.g. 23.12.2026)
    const fallbackDate = t('format:date', {
      postProcess: 'parseDate',
      value: data.date,
    });

    if (Number.isNaN(fallbackDate.getTime())) {
      return null;
    }

    return fallbackDate;
  }, [data.date, t, datePickerDateFormat]);

  const handleDatePickerChange = useCallback(
    (date) => {
      setData((prevData) => ({
        ...prevData,
        date: t('format:datePickerDate', {
          defaultValue: datePickerDateFormat,
          postProcess: 'formatDate',
          value: date,
        }),
      }));
      selectTimeField();
    },
    [setData, selectTimeField, t, datePickerDateFormat],
  );

  const handleSubmit = useCallback(() => {
    if (!nullableDate) {
      dateField.current.select();
      return;
    }

    const value = t('format:datePickerDateTime', {
      defaultValue: datePickerDateTimeFormat,
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (!Number.isNaN(value.getTime())) {
      if (!defaultValue || value.getTime() !== defaultValue.getTime()) {
        onUpdate(value);
      }

      onClose();
      return;
    }

    // Backward compatibility: allow previous locale-specific dateTime parsing
    const fallbackValue = t('format:dateTime', {
      postProcess: 'parseDate',
      value: `${data.date} ${data.time}`,
    });

    if (Number.isNaN(fallbackValue.getTime())) {
      timeField.current.select();
      return;
    }

    if (!defaultValue || fallbackValue.getTime() !== defaultValue.getTime()) {
      onUpdate(fallbackValue);
    }

    onClose();
  }, [
    defaultValue,
    onUpdate,
    onClose,
    data,
    nullableDate,
    t,
    datePickerDateTimeFormat,
  ]);

  const handleClearClick = useCallback(() => {
    if (defaultValue) {
      onUpdate(null);
    }

    onClose();
  }, [defaultValue, onUpdate, onClose]);

  useEffect(() => {
    dateField.current.select();
  }, []);

  useDidUpdate(() => {
    if(timeField.current)
      timeField.current.select();
  }, [selectTimeFieldState]);


  return (
    <>
      <Popup.Header onBack={onBack}>
        {t('common.editDueDate', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <Form onSubmit={handleSubmit}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.date')}</div>
              <Input ref={dateField} name="date" value={data.date} onChange={handleFieldChange} />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.time')}</div>
              <InputMask
                mask="99:99"
                placeholder="__:__"
                value={data.time}
                onChange={(e) => handleFieldChange(null, { name: "time", value: e.target.value })} // Исправлено!
              >
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
          <Button positive content={t('action.save')} />
        </Form>
        <Button
          negative
          content={t('action.remove')}
          className={styles.deleteButton}
          onClick={handleClearClick}
        />
      </Popup.Content>
    </>
  );
});

DueDateEditStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  onUpdate: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

DueDateEditStep.defaultProps = {
  defaultValue: undefined,
  onBack: undefined,
};

export default DueDateEditStep;
